import { useEffect, useState } from 'react';
import {
  LIBELLES_JUSTIFICATIF,
  TYPES_JUSTIFICATIF,
  justificatifsParDefaut,
  type BaremeApplicable,
  type GroupeBareme,
  type JustificatifDetail,
  type TypeJustificatif,
} from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

function jourLisible(jour: string): string {
  return jour.slice(0, 10).split('-').reverse().join('/');
}

function Etat({ justificatif }: { justificatif: JustificatifDetail }) {
  switch (justificatif.etat) {
    case 'recu':
      return <span className="signal-info">Reçu le {jourLisible(justificatif.recuLe ?? '')}</span>;
    case 'en_retard':
      return (
        <span className="signal-alerte">
          En retard de {Math.abs(justificatif.joursRestants ?? 0)} j (échéance {jourLisible(justificatif.attenduAvant ?? '')})
        </span>
      );
    case 'attendu':
      return (
        <span className={(justificatif.joursRestants ?? 0) <= 2 ? 'signal-alerte' : 'signal-info'}>
          Avant le {jourLisible(justificatif.attenduAvant ?? '')} ·{' '}
          {justificatif.joursRestants === 0 ? 'dernier jour' : `J-${justificatif.joursRestants}`}
        </span>
      );
    default:
      return <span className="mention">Échéance à la charte signée</span>;
  }
}

/**
 * Justificatifs (docs/ARCHITECTURE.md § 5.5) : liste attendue, dépôt de chaque
 * pièce, compte à rebours de sept jours après la charte signée.
 */
export function Justificatifs({ candidatureId, onChange }: { candidatureId: string; onChange: () => void }) {
  const [liste, setListe] = useState<JustificatifDetail[] | null>(null);
  const [groupe, setGroupe] = useState<GroupeBareme | null>(null);
  const [edition, setEdition] = useState<Map<TypeJustificatif, boolean> | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<JustificatifDetail[]>(`/candidatures/${candidatureId}/justificatifs`)
      .then(setListe)
      .catch((e: Error) => setErreur(e.message));
    appel<BaremeApplicable>(`/candidatures/${candidatureId}/bareme`)
      .then((bareme) => setGroupe(bareme.groupe))
      .catch(() => setGroupe(null));
  }, [candidatureId]);

  async function executer(cle: string, travail: () => Promise<JustificatifDetail[]>): Promise<void> {
    setErreur(null);
    setEnvoi(cle);
    try {
      setListe(await travail());
      onChange();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Opération impossible');
    } finally {
      setEnvoi(null);
    }
  }

  function deposer(type: TypeJustificatif, fichier: File | undefined): void {
    if (!fichier) return;
    const donnees = new FormData();
    donnees.append('fichier', fichier);
    void executer(type, () =>
      appel<JustificatifDetail[]>(`/candidatures/${candidatureId}/justificatifs/${type}`, {
        method: 'POST',
        body: donnees,
      }),
    );
  }

  function ouvrirEdition(): void {
    const actuelle = liste ?? [];
    const depart =
      actuelle.length > 0
        ? actuelle.map((j) => [j.type, j.obligatoire] as const)
        : justificatifsParDefaut(groupe ?? 'vente').map((j) => [j.type, j.obligatoire] as const);
    setEdition(new Map(depart));
  }

  function enregistrerEdition(): void {
    if (!edition) return;
    const justificatifs = [...edition.entries()].map(([type, obligatoire]) => ({ type, obligatoire }));
    void executer('definition', async () => {
      const resultat = await appel<JustificatifDetail[]>(`/candidatures/${candidatureId}/justificatifs`, {
        method: 'PUT',
        body: JSON.stringify({ justificatifs }),
      });
      setEdition(null);
      return resultat;
    });
  }

  if (!liste) return erreur ? <Erreur message={erreur} /> : <p className="mention">Chargement des justificatifs…</p>;

  return (
    <div>
      {liste.length === 0 && !edition && (
        <p className="mention">
          La liste des pièces se crée à l’enregistrement de la charte signée, avec son échéance de sept jours.
        </p>
      )}

      {!edition && liste.length > 0 && (
        <ul className="justificatifs">
          {liste.map((justificatif) => (
            <li key={justificatif.id}>
              <span className="libelle">
                {justificatif.libelle}
                <small className="mention">{justificatif.obligatoire ? ' · obligatoire' : ' · facultatif'}</small>
              </span>
              <Etat justificatif={justificatif} />
              <label className={`bouton-second lien-bouton bouton-fichier${envoi ? ' inactif' : ''}`}>
                {envoi === justificatif.type ? 'Envoi…' : justificatif.documentId ? 'Remplacer' : 'Déposer'}
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  disabled={envoi !== null}
                  onChange={(e) => {
                    deposer(justificatif.type, e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            </li>
          ))}
        </ul>
      )}

      {edition && (
        <ul className="justificatifs">
          {TYPES_JUSTIFICATIF.map((type) => {
            const demande = edition.has(type);
            const recu = liste.some((j) => j.type === type && j.documentId);
            return (
              <li key={type}>
                <label className="case">
                  <input
                    type="checkbox"
                    checked={demande}
                    disabled={recu}
                    onChange={(e) => {
                      const suivante = new Map(edition);
                      if (e.target.checked) suivante.set(type, false);
                      else suivante.delete(type);
                      setEdition(suivante);
                    }}
                  />
                  {LIBELLES_JUSTIFICATIF[type].fr}
                </label>
                {demande && (
                  <label className="case">
                    <input
                      type="checkbox"
                      checked={edition.get(type) === true}
                      onChange={(e) => setEdition(new Map(edition).set(type, e.target.checked))}
                    />
                    Obligatoire
                  </label>
                )}
                {recu && <span className="mention">déjà reçu</span>}
              </li>
            );
          })}
        </ul>
      )}

      {erreur && <Erreur message={erreur} />}
      <div className="actions-fiche">
        {edition ? (
          <>
            <button type="button" className="bouton" disabled={envoi !== null} onClick={enregistrerEdition}>
              Enregistrer la liste
            </button>
            <button type="button" className="lien" onClick={() => setEdition(null)}>
              Annuler
            </button>
          </>
        ) : (
          <button type="button" className="bouton-second" onClick={ouvrirEdition}>
            {liste.length === 0 ? 'Demander des justificatifs' : 'Modifier la liste'}
          </button>
        )}
      </div>
    </div>
  );
}
